import React, { useCallback, useState } from "react";

import * as Yup from "yup";
import { AiOutlineLock } from "react-icons/ai";
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io";
import { signOut } from "utils/functions";
import { useFormik } from "formik";
import { useHistory } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import { passwordRegex } from "utils/regex";

import Navbar from "components/MI/Navbar";
import TextField from "components/layout/form/textField";
import UserMenu from "components/layout/userMenuNew";

import ContainedButton from "components/layout/form/containedButton";
import CustomToolip from "./CustomToolip";
import PasswordForceMobile from "./PasswordForceMobile";

import CustomTooltipOldPassword from "./CustomTooltipOldPassword";
import OldPasswordMobile from "./OldPasswordMobile";
import OutlinedButton from "components/layout/form/outlinedButton";
import PasswordSuccessModal from "./PasswordSuccessModal";
import {
  Body,
  Container,
  ContainerButtons,
  Form,
  Header,
  HeaderInfos,
  HeaderSubtitle,
  HeaderTitle,
  PanelContainer,
  SearchDocumentIcon,
  SearchDocumentText,
  WrapSearchDocument,
} from "./styles";
import { updatePassword } from "./services";

const AlterarSenha = (): JSX.Element => {
  const [showPasswordForce, setShowPasswordForce] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showModalSuccess, setShowModalSuccess] = useState(false);

  const history = useHistory();

  const showMobileMode = useMediaQuery({ maxWidth: 959 });

  const validationSchema = (): any =>
    Yup.object().shape({
      password: Yup.string()
        .required("Insira uma senha")
        .matches(
          passwordRegex,
          "Use o mínimo de 8 caracteres, com uma combinação de letras maiúsculas, minúsculas, números e símbolos."
        ),
      passwordConfirmation: Yup.string()
        .required("Insira a confirmação de sua senha")
        .oneOf([Yup.ref("password"), null], "As senhas não combinam"),
      oldPassword: Yup.string().required("Insira sua senha antiga"),
    });

  const handleShowPasswordForce = useCallback(() => {
    setShowPasswordForce((prev) => !prev);
  }, [showPasswordForce]);

  const handleShowOldPassword = useCallback(() => {
    setShowOldPassword((prev) => !prev);
  }, [showPasswordForce]);

  const requestRegisterPassword = async (
    { password, passwordConfirmation, oldPassword },
    resetForm
  ): Promise<void> => {
    const response = await updatePassword({
      newPassword: password,
      newPasswordConfirmation: passwordConfirmation,
      password: oldPassword,
    });

    setShowModalSuccess(response || false);
    if (response) resetForm();
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      passwordConfirmation: "",
      oldPassword: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await requestRegisterPassword(values, resetForm);
    },
  });

  return (
    <Navbar selected="alterar-senha">
      <UserMenu />
      <Container>
        <Header>
          <HeaderInfos>
            <HeaderTitle>Alterar senha</HeaderTitle>
            <HeaderSubtitle>
              Sua senha deverá ser definida de acordo com os requisitos de
              segurança.
            </HeaderSubtitle>
          </HeaderInfos>
        </Header>
        <Body>
          <PanelContainer>
            <Form onSubmit={formik.handleSubmit}>
              <TextField
                id="oldPassword"
                name="oldPassword"
                type="oldPassword"
                label="Senha atual"
                placeholder="Senha atual"
                aria-label="Senha atual"
                aria-required
                errorCondition={
                  !!formik.errors.oldPassword && !!formik.touched.oldPassword
                }
                errorMessage={formik.errors.oldPassword}
                showChangeTextPassword
                onBlur={formik.handleBlur}
                value={formik.values.oldPassword}
                onChange={formik.handleChange}
                LeadingIcon={AiOutlineLock}
                propsLeadingIcon={{
                  size: 20,
                }}
              />

              <WrapSearchDocument>
                {!showMobileMode && (
                  <>
                    <SearchDocumentText
                      onClick={() => {
                        history?.push("/esqueci-senha-mi");
                        signOut(true);
                      }}
                    >
                      Esqueci minha senha
                    </SearchDocumentText>
                    <CustomTooltipOldPassword>
                      <SearchDocumentIcon>!</SearchDocumentIcon>
                    </CustomTooltipOldPassword>
                  </>
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  {showMobileMode && (
                    <div
                      style={{
                        display: "flex",
                      }}
                    >
                      <SearchDocumentText
                        onClick={() => {
                          history?.push("/esqueci-senha-mi");
                          signOut(true);
                        }}
                      >
                        Esqueci minha senha
                      </SearchDocumentText>
                      <SearchDocumentIcon onClick={handleShowOldPassword}>
                        !
                      </SearchDocumentIcon>
                    </div>
                  )}
                  {showMobileMode && showOldPassword && <OldPasswordMobile />}
                </div>
              </WrapSearchDocument>
              <TextField
                id="password"
                name="password"
                type="password"
                label="Nova senha"
                placeholder="Nova senha"
                aria-label="Nova senha"
                aria-required
                errorCondition={
                  !!formik.errors.password && !!formik.touched.password
                }
                errorMessage={formik.errors.password}
                showChangeTextPassword
                onBlur={formik.handleBlur}
                value={formik.values.password}
                onChange={formik.handleChange}
                LeadingIcon={AiOutlineLock}
                propsLeadingIcon={{
                  size: 20,
                }}
              />
              <WrapSearchDocument>
                {!showMobileMode && (
                  <CustomToolip title="Requisitos de Senha">
                    <SearchDocumentText>Requisitos de senha</SearchDocumentText>
                    <SearchDocumentIcon>!</SearchDocumentIcon>
                  </CustomToolip>
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  {showMobileMode && (
                    <div
                      style={{
                        display: "flex",
                      }}
                      onClick={handleShowPasswordForce}
                    >
                      <SearchDocumentText>
                        Requisitos de senha
                      </SearchDocumentText>
                      <SearchDocumentIcon>!</SearchDocumentIcon>
                    </div>
                  )}
                  {showMobileMode && showPasswordForce && (
                    <PasswordForceMobile />
                  )}
                </div>
              </WrapSearchDocument>
              <TextField
                id="passwordConfirmation"
                name="passwordConfirmation"
                type="password"
                label="Confirmação de Senha"
                placeholder="Confirmação de Senha"
                aria-label="Confirmação de Senha"
                aria-required
                errorCondition={
                  !!formik.errors.passwordConfirmation &&
                  !!formik.touched.passwordConfirmation
                }
                errorMessage={formik.errors.passwordConfirmation}
                showChangeTextPassword
                onBlur={formik.handleBlur}
                value={formik.values.passwordConfirmation}
                onChange={formik.handleChange}
                LeadingIcon={AiOutlineLock}
                propsLeadingIcon={{
                  size: 20,
                }}
              />
              <ContainerButtons>
                <ContainedButton
                  type="submit"
                  loading={formik.isSubmitting}
                  id="Avançar"
                  width="100%"
                  TrailingIcon={IoMdArrowForward}
                  propsTrailingIcon={{
                    size: 20,
                  }}
                >
                  Confirmar alteração
                </ContainedButton>
                <OutlinedButton
                  type="button"
                  loading={formik.isSubmitting}
                  id="cancel"
                  width="100%"
                  LeadingIcon={IoMdArrowBack}
                  propsLeadingIcon={{
                    size: 20,
                  }}
                  onClick={() => {
                    history?.push("/portal");
                  }}
                >
                  Cancelar
                </OutlinedButton>
              </ContainerButtons>
            </Form>
          </PanelContainer>
        </Body>
      </Container>
      <PasswordSuccessModal
        openModal={showModalSuccess}
        modalFunction={(toggle) => {
          setShowModalSuccess(toggle);
        }}
        text="Sua senha foi alterada com sucesso!"
      />
    </Navbar>
  );
};

export default AlterarSenha;
